/**
 * Centralized language/locale constants for the application.
 *
 * This is the SINGLE SOURCE OF TRUTH for supported languages in the frontend.
 * All frontend files should import from here instead of defining their own language constants.
 *
 * Note: This mirrors lib/i18n/constants.ts for API routes. Both files should be kept in sync.
 */

/**
 * All supported language codes in the application.
 * Order: French (default), English, Arabic, German
 */
export const SUPPORTED_LANGUAGES = ['fr', 'en', 'ar', 'de'] as const;

/**
 * Union type of all supported language codes.
 */
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/**
 * Human-readable names for each language in their native form.
 */
export const LANGUAGE_NATIVE_NAMES: Record<SupportedLanguage, string> = {
  fr: 'Français',
  en: 'English',
  ar: 'العربية',
  de: 'Deutsch',
};

/**
 * Human-readable names for each language in English.
 */
export const LANGUAGE_NAMES: Record<SupportedLanguage, string> = {
  fr: 'French',
  en: 'English',
  ar: 'Arabic',
  de: 'German',
};

/**
 * BCP 47 locale codes for each language (used for Intl APIs).
 */
export const LANGUAGE_LOCALE_CODES: Record<SupportedLanguage, string> = {
  fr: 'fr-FR',
  en: 'en-US',
  ar: 'ar-MA',
  de: 'de-DE',
};

/**
 * Languages that use right-to-left text direction.
 */
export const RTL_LANGUAGES: SupportedLanguage[] = ['ar'];

/**
 * AI system prompt instructions for each language.
 */
export const LANGUAGE_AI_INSTRUCTIONS: Record<
  SupportedLanguage,
  { name: string; instruction: string }
> = {
  fr: { name: 'French', instruction: 'Respond entirely in French.' },
  en: { name: 'English', instruction: 'Respond entirely in English.' },
  ar: {
    name: 'Arabic',
    instruction: 'Respond entirely in Arabic. Use right-to-left text direction.',
  },
  de: { name: 'German', instruction: 'Respond entirely in German.' },
};

/**
 * Default language for the application.
 */
export const DEFAULT_LANGUAGE: SupportedLanguage = 'fr';

/**
 * Type guard to check if a value is a supported language code.
 */
export function isSupportedLanguage(value: unknown): value is SupportedLanguage {
  return typeof value === 'string' && (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

/**
 * Check if a language uses right-to-left text direction.
 */
export function isRTL(language: SupportedLanguage): boolean {
  return RTL_LANGUAGES.includes(language);
}

/**
 * Get the text direction for a language.
 */
export function getDirection(language: SupportedLanguage): 'ltr' | 'rtl' {
  return isRTL(language) ? 'rtl' : 'ltr';
}
